import React, { useContext } from 'react';

import { isEmpty, startCase, uniqBy } from 'lodash';
import moment from 'moment';
import { func, string } from 'prop-types';
import { useMutation } from '@apollo/client';

import { captureException, ErrorHandler } from '@playbookux/playbook-web-core';
import {
  Column,
  Container,
  Data,
  Grid,
  Header,
  Link,
  NoDataMessage,
  Row,
} from './styles';

import {
  ClickableData,
  Loader,
} from '../../styles';

import { REPROCESS_VIDEO } from '../../../../../core/graphql/test-sessions';
import ReprocessLink from './reprocess-link';
import TestSessionContext from '../../test-session-context-provider';
import { registerFunctions } from '../../../../../core/hooks/use-function-handler';

const REPROCESS_TIMEOUT_TIME = 230000;

const Completed = (props) => {
  const {
    onSetCurrentTester,
    testType,
    refetch,
    projectId,
  } = props;
  const [reprocessVideo] = useMutation(REPROCESS_VIDEO, {
    onCompleted: refetch,
  });
  const {
    completedTesters,
    testSessionStatuses,
    setTestSessionStatuses,
    loadingCompletedTesters,
    errorCompletedTesters,
    refetchCompletedTesters,
  } = useContext(TestSessionContext);

  registerFunctions(`refetch_test_sessions_${projectId}`, {
    func2: refetchCompletedTesters,
  });

  const handleReprocessVideo = async (id) => {
    try {
      setTestSessionStatuses(
        (prev) => ({ ...prev, [id]: 'enqueued' }),
      );
      await reprocessVideo({
        variables: {
          testSessionId: id,
        },
      });
    } catch (e) {
      captureException('HANDLE_REPROCESS_VIDEO_COMPLETED_FAILED', e);
    } finally {
      setTimeout(
        () => {
          setTestSessionStatuses((prev) => {
            if (prev[id] === 'enqueued') {
              return { ...prev, [id]: 'timedOut' };
            }
            return prev;
          });
        }, REPROCESS_TIMEOUT_TIME,
      );
    }
  };

  const tsReprocessLoading = (testSessionId) => {
    const processStatus = testSessionStatuses[testSessionId];
    return processStatus === 'enqueued' || processStatus === 'processing';
  };

  if (loadingCompletedTesters) {
    return <Loader size="small" data-loader="completed-menu-loader" />;
  }

  if (errorCompletedTesters) {
    return (
      <ErrorHandler
        error={errorCompletedTesters}
        defaultMessage="Error while retrieving the testers."
      />
    );
  }

  const completedTestByUniqueId = uniqBy(completedTesters, 'id');

  return (
    <Container>
      <Grid column={5}>
        <Row>
          <Column width={3}>
            <Header>Tester</Header>
          </Column>

          <Column width={3}>
            <Header>Completed On</Header>
          </Column>

          <Column width={2}>
            <Header>Payment Type</Header>

          </Column>

          <Column width={2}>
            <Header>Payment Address</Header>
          </Column>

          <Column width={2}>
            <Header>Status</Header>
          </Column>
        </Row>

        {isEmpty(completedTestByUniqueId)
          && <NoDataMessage>No completed test sessions found</NoDataMessage>}

        {!isEmpty(completedTestByUniqueId) && completedTestByUniqueId.map((tester) => {
          const { testSessions, user, id } = tester;
          const {
            profile,
          } = user;
          const { paymentMethod, paypalAccount, venmoAccount } = profile;

          return testSessions.map((testSession) => (
            <Row key={`${id}-${testSession.id}`}>
              <Column width={3}>
                <ClickableData onClick={() => onSetCurrentTester(tester)}>
                  {`${profile.firstName} ${profile.lastName}`}
                </ClickableData>
              </Column>

              <Column width={3}>
                <Data>
                  {testSession.completedAt && moment(testSession.completedAt).format('MM/DD/YYYY hh:mm A')}
                </Data>
              </Column>

              <Column width={2}>
                <Data>
                  {startCase(paymentMethod)}
                </Data>
              </Column>

              <Column width={2}>
                <Data>
                  {paymentMethod === 'paypal' ? paypalAccount : venmoAccount}
                </Data>
              </Column>

              <Column width={2}>
                <Data>
                  {testSessionStatuses[testSession.id] === 'timedOut' ? 'Timed Out'
                    : startCase(testSessionStatuses[testSession.id] || testSession.status)}
                </Data>
              </Column>

              <Column width={4} lastChild>
                {(testType === 'unmoderated' || testType === 'moderated') && (
                  <>
                    {testSession.status !== 'processed'
                        && (
                          <ReprocessLink
                            testSession={testSession}
                            loading={tsReprocessLoading(testSession.id)}
                            onClick={handleReprocessVideo}
                          />
                        )}
                    <Link href={testSession.videoUrl} download data-converted-key={(!!testSession?.convertedVideoKey)}>
                      Download
                    </Link>
                    ,&nbsp;
                  </>
                )}
                <Link onClick={() => navigator.clipboard.writeText(testSession.id)}>
                  Copy Tester Session ID
                </Link>
              </Column>
            </Row>
          ));
        })}
      </Grid>
    </Container>
  );
};

Completed.propTypes = {
  testType: string,
  onSetCurrentTester: func.isRequired,
  refetch: func.isRequired,
  projectId: string.isRequired,
};

Completed.defaultProps = {
  testType: null,
};

export default Completed;
